import type { ActionCue, Beat } from '../data/cutscenes'
import { CUTSCENE_FOOD_CONTACT, CUTSCENE_THROW_RELEASE } from '../data/cutscenes'
import { getSettings } from '../services/settings'

type SoundFamily = 'coin' | 'wood' | 'electric' | 'bubble' | 'magic' | 'swallow' | 'chirp' | 'storm' | 'music' | 'ambience'
export interface SoundCue { family: SoundFamily; label: string; duration?: number; quiet?: boolean }
export interface CinematicSoundSink { play(cue: SoundCue): void; stop(): void; dispose(): void }

function family(label: string): SoundFamily {
  if (/coin|spoon/i.test(label)) return 'coin'
  if (/wood|creak|roll|deck/i.test(label)) return 'wood'
  if (/buzz|electri|lightning|zap|charge/i.test(label)) return 'electric'
  if (/chord|dissolv|shudder/i.test(label)) return 'magic'
  if (/swallow/i.test(label)) return 'swallow'
  if (/chirp/i.test(label)) return 'chirp'
  if (/storm|current/i.test(label)) return 'storm'
  return 'bubble'
}

const FOOD_CONTACT = CUTSCENE_FOOD_CONTACT

function contactAt(action: ActionCue | undefined, shot: string): number {
  if (!action) return 0
  const food = /sandwich|patty|jelly/i.test(action.prop ?? '')
  if (action.type === 'throw') return Math.max(action.delay ?? 0, CUTSCENE_THROW_RELEASE)
  if (action.type === 'catch' || ((action.type === 'eat' || action.type === 'swallow') && food)) {
    return FOOD_CONTACT[shot] ?? action.delay ?? 0
  }
  return action.delay ?? 0
}

function actionForLabel(beat: Beat, label: string): ActionCue | undefined {
  if (/toss|throw/i.test(label)) return beat.actions.find(action => action.type === 'throw')
  if (/catch/i.test(label)) return beat.actions.find(action => action.type === 'catch') ?? beat.actions.find(action => action.type === 'eat')
  if (/swallow/i.test(label)) return beat.actions.find(action => action.type === 'swallow' || action.type === 'eat')
  if (/pickup|bag lift|serve/i.test(label)) return beat.actions.find(action => action.type === 'collect')
  if (label === 'electrical arcs') return beat.actions.find(action => action.type === 'command-pulse')
  return undefined
}

/** One beat owns all its scheduled cues; progression, not timers, triggers contact sounds. */
export class CutsceneSound {
  private pending: Array<{ at: number; cue: SoundCue }> = []
  private muted = false
  private progress = 0
  private ambience: SoundCue | null = null
  constructor(private sink: CinematicSoundSink = new WebAudioCinematicSink()) {}

  startBeat(beat: Beat): void {
    this.stop()
    const duration = beat.duration ?? Math.max(3, (beat.dialogue?.text.length ?? 0) / 13)
    if (!beat.audio?.silence) {
      this.ambience = { family: 'ambience', label: beat.scene, duration, quiet: true }
      this.pending.push({ at: 0, cue: this.ambience })
      if (beat.audio?.music) this.pending.push({ at: 0, cue: { family: 'music', label: beat.audio.music, quiet: !!beat.dialogue } })
    }
    for (const [index, label] of (beat.audio?.sfx ?? []).entries()) {
      const action = actionForLabel(beat, label) ?? beat.actions[Math.min(index, beat.actions.length - 1)]
      this.pending.push({ at: contactAt(action, beat.shot), cue: { family: family(label), label, quiet: !!beat.dialogue } })
    }
    // Untagged physical beats still make contact sounds. Explicit silence suppresses these.
    if (!beat.audio?.silence && !beat.audio?.sfx?.length) {
      for (const action of beat.actions) {
        let label: string | undefined
        if (['count', 'collect', 'spill', 'offer-coin'].includes(action.type) && /coin/.test(action.prop ?? '')) label = 'coin contact'
        else if (['summon-lightning', 'command-pulse', 'glitch'].includes(action.type)) label = 'electrical pulse'
        else if (['pop-bubble', 'form-bubble', 'throw', 'catch'].includes(action.type)) label = 'soft bubble pop'
        else if (['break-spell', 'soften-glow', 'sever-command'].includes(action.type)) label = 'warm resolving chord'
        else if (['eat', 'swallow'].includes(action.type)) label = 'single swallow'
        else if (['tilt', 'collapse-throne', 'knock-over'].includes(action.type)) label = 'wooden creak'
        if (label) this.pending.push({ at: contactAt(action, beat.shot), cue: { family: family(label), label, quiet: !!beat.dialogue } })
      }
    }
    this.update(0)
  }

  update(progress: number): void {
    this.progress = progress
    const ready = this.pending.filter(event => event.at <= progress)
    this.pending = this.pending.filter(event => event.at > progress)
    if (!this.muted) for (const event of ready) this.sink.play(event.cue)
  }
  setMuted(muted: boolean): void {
    if (this.muted === muted) return
    this.muted = muted
    if (muted) this.sink.stop()
    else if (this.ambience && this.progress < 1) {
      this.sink.play({ ...this.ambience, duration: (this.ambience.duration ?? 4) * (1 - this.progress) })
    }
  }
  stop(): void { this.pending = []; this.ambience = null; this.progress = 0; this.sink.stop() }
  dispose(): void { this.stop(); this.sink.dispose() }
}

/** Material-specific synthesized foley, with finite sources and no background timers. */
class WebAudioCinematicSink implements CinematicSoundSink {
  private ctx: AudioContext | null = null
  private sources = new Map<AudioScheduledSourceNode, AudioNode[]>()
  private context(): AudioContext | null {
    if (!this.ctx && typeof window !== 'undefined') {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (Ctor) this.ctx = new Ctor()
    }
    if (this.ctx?.state === 'suspended') void this.ctx.resume().catch(() => {})
    return this.ctx
  }
  play(cue: SoundCue): void {
    const ctx = this.context()
    if (!ctx) return
    const settings = getSettings()
    const bed = cue.family === 'ambience' || cue.family === 'music'
    const volume = settings.master * (bed ? settings.music : settings.sfx) * (cue.quiet ? 0.5 : 1)
    if (volume <= 0) return
    const tone = (hz: number, end: number, duration: number, amplitude: number, delay = 0, type: OscillatorType = 'sine') => {
      const source = ctx.createOscillator()
      source.type = type
      const t = ctx.currentTime + delay
      source.frequency.setValueAtTime(hz, t)
      source.frequency.exponentialRampToValueAtTime(Math.max(20, end), t + duration)
      this.envelope(source, [], t, duration, amplitude * volume)
    }
    const noise = (duration: number, hz: number, amplitude: number) => {
      const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * duration), ctx.sampleRate)
      const values = buffer.getChannelData(0)
      let seed = 13007
      for (let i = 0; i < values.length; i++) { seed = (seed * 16807) % 2147483647; values[i] = seed / 1073741824 - 1 }
      const source = ctx.createBufferSource()
      source.buffer = buffer
      const filter = ctx.createBiquadFilter()
      filter.type = 'lowpass'; filter.frequency.value = hz
      this.envelope(source, [filter], ctx.currentTime, duration, amplitude * volume)
    }
    switch (cue.family) {
      case 'coin':
        for (let i = 0; i < (/ratt|rapid|accelerat/i.test(cue.label) ? 7 : 3); i++) {
          tone(2200 + i * 193, 1800 + i * 170, 0.12, 0.09, i * 0.065, 'triangle')
          tone(3300 + i * 127, 2800, 0.07, 0.035, i * 0.065)
        }
        break
      case 'wood': noise(0.22, 600, 0.28); tone(150, 55, 0.32, 0.12, 0, 'triangle'); break
      case 'electric': noise(0.38, 3100, 0.08); tone(75, 48, 0.65, 0.12, 0, 'sawtooth'); tone(720, 150, 0.23, 0.04); break
      case 'bubble': tone(410, 85, 0.13, 0.18); tone(900, 340, 0.07, 0.05, 0.04); break
      case 'swallow': tone(180, 55, 0.21, 0.16); noise(0.08, 340, 0.1); break
      case 'chirp': tone(1350, 2300, 0.11, 0.055); tone(1800, 950, 0.14, 0.04, 0.16); break
      case 'magic': [523.25, 659.25, 783.99].forEach((hz, i) => tone(hz, hz * 1.01, 0.7, 0.07, i * 0.11)); break
      case 'music': [392, 493.88, 587.33, 783.99].forEach((hz, i) => tone(hz, hz, 0.8, 0.045, i * 0.3, 'triangle')); break
      case 'storm': noise(1.8, 1100, 0.16); tone(43, 26, 1.3, 0.1); break
      case 'ambience': noise(Math.min(cue.duration ?? 4, 18), /deepsea/.test(cue.label) ? 180 : /storm/.test(cue.label) ? 700 : 400, 0.028); break
    }
  }
  private envelope(source: AudioScheduledSourceNode, filters: AudioNode[], at: number, duration: number, volume: number): void {
    const ctx = this.ctx!
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(volume, at + Math.min(0.018, duration * 0.1))
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration)
    let previous: AudioNode = source
    for (const filter of filters) { previous.connect(filter); previous = filter }
    previous.connect(gain); gain.connect(ctx.destination)
    const nodes = [...filters, gain]
    this.sources.set(source, nodes)
    source.onended = () => { source.disconnect(); nodes.forEach(node => node.disconnect()); this.sources.delete(source) }
    source.start(at); source.stop(at + duration + 0.02)
  }
  stop(): void {
    for (const [source, nodes] of this.sources) {
      source.onended = null
      try { source.stop() } catch { /* already ended */ }
      source.disconnect(); nodes.forEach(node => node.disconnect())
    }
    this.sources.clear()
  }
  dispose(): void { this.stop(); void this.ctx?.close(); this.ctx = null }
}
